/* eslint-disable sort-keys */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocalizationContext } from '@im/react-localization';
import { useBuildVars, useInX4AFrame, useSiteConfig } from 'mf_core/config';
import { injectScriptInHeadWith } from './utils';

export function useOneTrust() {
  const { buildEnvironment } = useBuildVars();
  const { siteCode } = useSiteConfig();
  const { locale } = useLocalizationContext();
  const { isInIM360Frame, isInX4AFrame } = useInX4AFrame();
  const isScriptInjected = useRef(false);
  const win = window as any;

  //Updates Script in Head and needs to be synchronous
  useLayoutEffect(() => {
    //When Opening X4C/X4A inside an iFrame in CRM/IM360 OneTrust SDK is unable to read the cookies stored in
    //the localstorage since CRM domain and X4C domain are different and browser is not allowing access.
    //Added This Effect to prevent injecting OneTrust Scripts in CRM/IM360 iFrame screen againt BUG ADT-15210
    if (!isScriptInjected.current && !isInIM360Frame && !isInX4AFrame) {
      let scriptId = '';
      if (buildEnvironment && siteCode) {
        const envMap = scriptsMap[buildEnvironment];
        if (envMap) {
          scriptId = envMap[siteCode];
        }
      }
      if (!isScriptInjected.current && scriptId) {
        loadOneClickCSS();
        injectScriptInHeadWith(
          `https://cdn.cookielaw.org/consent/${scriptId}/OtAutoBlock.js`
        );
        injectScriptInHeadWith(
          'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js',
          undefined,
          scriptId
        );
        injectScriptInHeadWith(
          undefined,
          `function OptanonWrapper() { window.dispatchEvent(new Event('bannerLoaded')); }`
        );
        isScriptInjected.current = true;
        if (window as any) (window as any).isOneTrustEnabled = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildEnvironment, siteCode]);

  useEffect(() => {
    if (isScriptInjected.current && win?.OneTrust?.changeLanguage) {
      win.OneTrust?.changeLanguage(locale.slice(0, 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);
}

/** Return `loading` at first and then sends script id string or `empty` string once ready*/
export function useIsOneTrustEnabled() {
  const { buildEnvironment } = useBuildVars();
  const { siteCode } = useSiteConfig();
  const [hasId, setHasId] = useState<string | undefined>('loading');

  useEffect(() => {
    let scriptId = '';
    if (buildEnvironment && siteCode) {
      const envMap = scriptsMap[buildEnvironment];
      if (envMap) {
        scriptId = envMap[siteCode];
      }
    }
    if (scriptId) {
      setHasId(scriptId);
    } else {
      setHasId('');
    }
  }, [buildEnvironment, siteCode]);

  return hasId;
}

const scriptsMap: any = {
  development: {
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88-test',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b-test',
  },
  production: {
    at: '44dd8255-aa4b-4557-b80b-2a24ea659e53',
    au: 'bf88a6d4-2148-404e-8c96-e5dd58f4805d',
    be: '0b0037df-0851-4896-aa89-9d19ed8890dd',
    br: '0565517d-8776-4bde-bb6e-d575cebdb4d2',
    ca: '44ec1e95-68da-44a7-863e-82f4106d645e',
    ch: 'fd420875-1423-4cee-b31c-029a1e120ca5',
    cl: '4729ebea-73db-4cfe-b7e3-088a5c758694',
    co: 'd7f6df39-f565-4244-8afa-55852a43265d',
    de: 'fa481d81-76c4-45d3-9136-c5482c0363ae',
    es: 'aca353d8-ce1d-42b9-a0d8-013115fab766',
    fr: 'e355c5ab-f65e-43ae-b5c9-63ef3090fa1e',
    gb: '6b727968-81a4-44e2-bcf4-c223368323ae',
    hk: '5ba4d092-59de-4ff9-bd08-9a4b85ad9925',
    id: '88d87685-64de-4492-93d7-d4c0e5a41ec5',
    in: '0537784a-5bde-4b36-a534-68183186040c',
    it: '057e1217-f062-4d5c-a4c7-13a14dabaaac',
    mx: 'b4449d97-8e60-4e5d-8955-4cceec4ad8df',
    my: '82984dfd-1131-4286-9d56-b1ff935c20e7',
    nl: '946f4467-f8b2-497d-aeba-55bf5e5484be',
    nz: '4bc45039-3f8f-48e5-8e27-b67f08a7456e',
    pe: '550a2f66-7655-4c11-b251-338050840121',
    sg: 'b412ece9-d419-4990-a25c-e052c6fe5c70',
    us: '99ef7803-f71a-4a5a-ab1e-21f585d4b3de',
    usme: '164deb5f-2b4a-4d8e-83f6-609e2ed95978',
  },
  productionbeta: {
    at: 'a0b222df-4519-4a6d-8719-bb2f8822e74b',
    au: '15eac479-bc68-4963-8ac6-ee98bb301024',
    be: 'bba8f146-e67f-4903-8b80-717590ab150b',
    br: '4a93a70b-697f-4f0a-8610-cf65263820a4',
    ca: 'f7e521f6-1665-4f7d-9447-7bc6bf7c16af',
    ch: '300e9251-18a8-4b83-9ba5-7b84ca09b879',
    cl: '9a22cd8b-f862-4f8b-b7d1-24cfa6e6f6f1',
    cn: '0f1d96db-5406-4f27-9f4d-9069f62fe734',
    co: '5fabb3de-ea9b-4052-b476-3b4665473525',
    de: '05e95e92-9c0a-4ae3-b52d-aef50bc0b84b',
    dk: 'bb774640-b08b-4c41-a796-a30b0bcc0cb4',
    es: 'd1ccb41c-3ac8-4746-bdac-0f0b090b0b51',
    fi: '650d3abf-8138-45de-86bf-6efb2a8e9a45',
    fr: '1e116878-c4fb-4cbf-b992-777419629dc8',
    gb: 'ea831300-7839-414d-82df-8f96a0e2db91',
    hk: 'd504548e-4204-4c39-8e31-289a3590dfc0',
    hu: '2d1cb494-1ecf-4a46-a0ac-f9130fc0a1d8',
    id: '084e9439-8500-43e1-aa93-a8ecfa9f5e34',
    in: 'cc6cd377-80f0-4877-859c-924c2f5c5011',
    it: 'b2556e1c-ff2d-4fbb-bcea-887fdca1c4b6',
    mx: 'b0144b6a-7506-4cb7-8b7f-f6b3551b06a7',
    my: 'a235d6d9-b775-4e3f-94a1-f21877b1359c',
    nl: '3ae234b2-a8bc-4b40-88b6-308c7762b6e3',
    nz: 'a1c46dd5-17c7-4c18-8657-6e065eba6f31',
    pe: 'f364691f-43a1-4d3a-8cce-9b02e76b48e6',
    pt: '5f349b16-a1a4-430a-be83-54d90897448f',
    se: '10793762-afe5-4b3b-8455-7ed5cae4c349',
    sg: '85cd5b2c-f678-41ce-8ca0-8f000c19bc00',
    th: '33bed5fb-a581-4706-96c1-b58d93648747',
    us: 'a13a040c-af96-4a32-a274-68a0216bbfdf',
    usme: '4506acef-75b5-47d3-9e73-8f2f6182a70a',
  },
  productionbetax: {
    at: 'a78265a4-663b-4fab-ba8f-cd41c1684eb1',
    au: 'badc1fa5-f48c-4d3b-98c4-5e82a60b58ee',
    be: '26e6bc4a-2a72-4f9e-926d-0aa227845b06',
    br: '9127f432-ae12-4f5e-801e-b112cdd57902',
    ca: 'a549e1ab-0ccf-4cd1-912b-440ecbc5e526',
    ch: '56484009-0918-4c6f-b468-d6a64bc90068',
    cl: '65807e11-d93b-4894-aed7-05d7f96c9022',
    cn: '8b6d47bc-d433-4312-8c1e-9b37f1ab4e6f',
    co: '95184fb7-2e26-4c25-a5d2-dc2a401e5098',
    de: '12f119ce-6a0e-4f93-95a1-6c11020fa7c2',
    dk: '82d09da8-5e06-41a4-9b14-09ea96e89b2e',
    es: 'f5897400-db93-478e-99d1-76ddd6186a71',
    fi: 'cf42f52d-8824-483e-ad73-d77a04222893',
    fr: '8bbd3040-f8ea-4646-8791-d12a00e9b2d3',
    gb: 'e675edc0-1f77-48ef-87f7-f77d09713c34',
    hk: '6b6dcb44-aae4-4cc7-9fdc-8331f160313b',
    hu: '5257492b-01be-46e6-9910-18e8b62c57e8',
    id: '78832b87-c9e7-4525-a08c-0f44fa5ac3df',
    in: 'fe177e0e-c058-41fc-89e4-abc77d4cf4eb',
    it: 'da82b083-11fd-44f6-a2e8-029a12eb2f11',
    mx: '84b53949-1003-4d42-a019-62e8b7a20e07',
    my: '36d9c188-b94b-45bc-99f4-9594abb480cb',
    nl: 'aca58b6d-4af4-4773-8ac1-2c69e9a57d18',
    nz: '3572b260-ab31-4330-af92-f461660c6855',
    pe: '9e47e965-2f35-49f8-b3c6-620b17498d31',
    pt: 'fbb5a01a-2bc1-4941-a019-2d188ccd3063',
    se: '529a171e-f19b-4529-becc-b4dbdf3d246d',
    sg: 'ea9c8552-42e0-4a47-96ae-6e920e58bda0',
    th: 'f696c8c0-f434-4434-8a53-9da1d0898809',
    us: '82bc1bf6-cadb-4710-aa0f-1ab7a86527ae',
    usme: '63e9ac78-9fcc-4fc9-b0e0-3d6db513ff68',
  },
  staging: {
    sg: '0190e350-3e9e-76ef-9473-2f647ba9adac',
    hk: '0190e352-9455-7378-ad82-f58e6f44c1b6',
    id: '0190e35e-96d4-7ca2-b254-e56f662ac84c',
    nz: '0190e366-fbec-776f-8cb2-08a4fc55e311',
    br: '0190e36a-7658-7ffa-89b5-8fd8d2a5958e',
    mi: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
    pe: '0190e378-9c5c-7d78-8ae7-38240c480bbb',
    cl: '0190e37a-760c-79d5-aa1a-269932e73b42',
    co: '0190e37d-1ff3-7172-8592-7b79c4ff929b',
    at: '63148719-cda6-491f-9737-d7a53edc5d42',
    nl: '1a5c451e-b7a2-4439-84cd-acaf579c9149',
    be: '8c070da0-9422-440c-94b3-6bb589844324',
    ch: '0190e39c-705a-7fb8-a00f-69ae5ba3ccac',
    au: 'f31ce79d-fdd9-491f-bdf5-2539c34a1fc5',
    ca: 'f0fa73fd-2424-40b3-8b9d-b6d0795a71ea',
    cn: '68c9a61c-5582-495f-b447-9518c6a0c5bf',
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88',
    dk: '1dc736f6-747e-489a-b63d-99c3d579ae0d',
    es: 'da79cfbb-7673-4761-b5eb-c9fcb6a8f1fd',
    fi: '58c78875-00a1-468d-b690-2d7feea0df81',
    fr: '826d79e4-068f-4dbf-a5d0-25a2ca7a8316',
    gb: '009a5a44-7658-4e58-b93c-3920cfe31961',
    hu: '3cdd9702-b7c9-4078-8564-a975dab79ac3',
    in: '337ba5fc-3a4c-4d14-94b5-23235ce52259',
    it: '959211c2-d26d-4248-9be6-3653b636c5d9',
    mx: '62854f5a-0804-4a50-9b06-e57c424d5d04',
    my: '5fe7808e-9d34-4b79-a522-cf404f882753',
    pt: '0258463a-9827-4ae3-b982-d5c5108c1fbd',
    se: 'ccd5f5d6-dd0a-47da-98f0-06a2800f5199',
    th: '8d30ab41-5f11-426e-9382-d5bd1a5729af',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b',
    usme: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
  },
  stagingx: {
    sg: '0190e350-3e9e-76ef-9473-2f647ba9adac',
    hk: '0190e352-9455-7378-ad82-f58e6f44c1b6',
    id: '0190e35e-96d4-7ca2-b254-e56f662ac84c',
    nz: '0190e366-fbec-776f-8cb2-08a4fc55e311',
    br: '0190e36a-7658-7ffa-89b5-8fd8d2a5958e',
    mi: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
    pe: '0190e378-9c5c-7d78-8ae7-38240c480bbb',
    cl: '0190e37a-760c-79d5-aa1a-269932e73b42',
    co: '0190e37d-1ff3-7172-8592-7b79c4ff929b',
    at: '63148719-cda6-491f-9737-d7a53edc5d42',
    nl: '1a5c451e-b7a2-4439-84cd-acaf579c9149',
    be: '8c070da0-9422-440c-94b3-6bb589844324',
    ch: '0190e39c-705a-7fb8-a00f-69ae5ba3ccac',
    au: 'f31ce79d-fdd9-491f-bdf5-2539c34a1fc5',
    ca: 'f0fa73fd-2424-40b3-8b9d-b6d0795a71ea',
    cn: '68c9a61c-5582-495f-b447-9518c6a0c5bf',
    de: '697b043e-f485-43da-a6a6-2734a4ec8b88',
    dk: '1dc736f6-747e-489a-b63d-99c3d579ae0d',
    es: 'da79cfbb-7673-4761-b5eb-c9fcb6a8f1fd',
    fi: '58c78875-00a1-468d-b690-2d7feea0df81',
    fr: '826d79e4-068f-4dbf-a5d0-25a2ca7a8316',
    gb: '009a5a44-7658-4e58-b93c-3920cfe31961',
    hu: '3cdd9702-b7c9-4078-8564-a975dab79ac3',
    in: '337ba5fc-3a4c-4d14-94b5-23235ce52259',
    it: '959211c2-d26d-4248-9be6-3653b636c5d9',
    mx: '62854f5a-0804-4a50-9b06-e57c424d5d04',
    my: '5fe7808e-9d34-4b79-a522-cf404f882753',
    pt: '0258463a-9827-4ae3-b982-d5c5108c1fbd',
    se: 'ccd5f5d6-dd0a-47da-98f0-06a2800f5199',
    th: '8d30ab41-5f11-426e-9382-d5bd1a5729af',
    us: '79356f9e-23dd-4222-b2de-b0c37f4d5a5b',
    usme: '0190e376-b8d2-79ce-86ad-b5c0d5d25bdf',
  },
};

/** loads onetrust css */
export function loadOneClickCSS() {
  return new Promise<boolean>(function (resolve, reject) {
    try {
      const styleEl = document.createElement('style');
      styleEl.innerHTML =
        '#onetrust-consent-sdk *:focus,#onetrust-consent-sdk *:active,#onetrust-pc-sdk .ot-tgl input:focus+.ot-switch .ot-switch-nob {outline: none !important;}';

      styleEl.onload = () => resolve(true);
      styleEl.onerror = () => resolve(false);
      document.head.appendChild(styleEl);
    } catch (e) {
      resolve(false);
    }
  });
}
